import React, { useCallback, useMemo, useState, memo } from 'react';
import classNames from 'classnames';
import Icon from '../../../../lib/icon';
import TextField from '../../text-field';
import CheckBox from '../../check-box';
import { $l } from '../../locale-context';
import { fieldIsDisabled } from './TableDynamicFilterBar';
const FieldList = function FieldList({ value, onSelect, onUnSelect, groups, prefixCls, queryDataSet }) {
    const [searchText, setSearchText] = useState('');
    const codes = useMemo(() => groups.reduce((res, current) => [...res, ...current.fields.map((o) => {
            const hasBindProps = (propsName) => o && o.get(propsName) && o.get(propsName).bind;
            if (!o.get('bind') &&
                !hasBindProps('computedProps') &&
                !hasBindProps('dynamicProps')) {
                return o.get('name');
            }
            return undefined;
        })], []), [groups]);
    const labelCodes = useMemo(() => groups.reduce((res, current) => [...res, ...current.fields.map((o) => {
            const hasBindProps = (propsName) => o && o.get(propsName) && o.get(propsName).bind;
            if (!o.get('bind') &&
                !hasBindProps('computedProps') &&
                !hasBindProps('dynamicProps')) {
                return [o.get('name'), o.get('label')];
            }
            return undefined;
        })], []), [groups]);
    const hasSelect = useMemo(() => {
        const isSelect = labelCodes.some(lc => {
            if (lc && (lc[1] && lc[1].includes(searchText || '') || lc[1] === undefined)) {
                return value.includes(lc[0]);
            }
            return false;
        });
        return isSelect;
    }, [value.length, searchText]);
    const hasSelectAll = useMemo(() => {
        const isAll = labelCodes.some(lc => {
            if (lc && (lc[1] && lc[1].includes(searchText || '') || lc[1] === undefined)) {
                return !value.includes(lc[0]);
            }
            return false;
        });
        return !isAll;
    }, [searchText, value.length]);
    const isChecked = useCallback((code) => value.includes(code), [value]);
    const handleChange = useCallback((code, select) => {
        if (select) {
            onSelect(code);
        }
        else {
            onUnSelect(code);
        }
    }, [onSelect, onUnSelect]);
    const renderGroup = useCallback((group) => group.fields.length > 0 && (React.createElement("div", { className: `${prefixCls}-section`, key: group.title },
        React.createElement("div", { className: `${prefixCls}-list` }, group.fields.map((field) => {
            const code = field.get('name');
            const label = field.get('label') || code;
            const checked = isChecked(code);
            const disabled = queryDataSet && fieldIsDisabled(field, queryDataSet);
            const hasBindProps = (propsName) => field && field.get(propsName) && field.get(propsName).bind;
            if (label && label.includes(searchText || '') &&
                !field.get('bind') &&
                !hasBindProps('computedProps') &&
                !hasBindProps('dynamicProps')) {
                const itemClassNames = classNames(`${prefixCls}-item`, {
                    [`${prefixCls}-item-disabled`]: disabled,
                });
                return (React.createElement("div", { className: itemClassNames, key: code },
                    React.createElement(CheckBox, { value: code, checked: checked, onChange: () => handleChange(code, !checked) }, label)));
            }
            return null;
        })))), [handleChange, isChecked, searchText, queryDataSet]);
    const selectItems = value.filter(v => codes.includes(v));
    return (React.createElement("div", { className: prefixCls },
        React.createElement("div", { className: `${prefixCls}-search` },
            React.createElement(TextField, { style: { flex: 1 }, value: searchText, onClick: (e) => {
                    e.preventDefault();
                    e.currentTarget.focus();
                }, onChange: (v) => {
                    setSearchText(v);
                }, onInput: (e) => {
                    setSearchText(e.target.value);
                }, prefix: React.createElement(Icon, { type: "search" }), placeholder: $l('Table', 'enter_text_filter'), clearButton: true })),
        React.createElement("div", { className: `${prefixCls}-header` },
            React.createElement("span", { className: `${prefixCls}-search-selected` },
                $l('Screening', 'selected'),
                React.createElement("span", { className: `${prefixCls}-search-items` }, selectItems.length),
                $l('Transfer', 'items')),
            React.createElement("span", { className: `${prefixCls}-search-divide` }),
            React.createElement("span", { style: { display: !hasSelectAll ? 'inline-block' : 'none' }, className: `${prefixCls}-search-action`, onClick: () => {
                    const values = labelCodes.map(lc => {
                        if (lc && (lc[1] && lc[1].includes(searchText || '') || lc[1] === undefined)) {
                            return lc[0];
                        }
                        return undefined;
                    }).filter(v => !!v);
                    handleChange(values, true);
                } }, $l('Select', 'select_all')),
            React.createElement("span", { style: { display: hasSelect ? 'inline-block' : 'none' }, className: `${prefixCls}-search-action`, onClick: () => {
                    const values = labelCodes.map(lc => {
                        if (lc && (lc[1] && lc[1].includes(searchText || '') || lc[1] === undefined)) {
                            return lc[0];
                        }
                        return undefined;
                    }).filter(v => !!v);
                    handleChange(values, false);
                } }, $l('Table', 'clear_filter'))),
        React.createElement("div", { className: `${prefixCls}-content` }, groups.map((group) => renderGroup(group)))));
};
FieldList.displayName = 'FieldList';
export default memo(FieldList);
